import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Loader2, Plus, Edit, Trash2, Send, ArrowUp, ArrowDown, GripVertical, Link2, FileCheck, Inbox } from 'lucide-react';
import { intake as intakeApi } from '@/lib/api';
import { toast } from 'sonner';

const FIELD_TYPES = [
  { v: 'text', l: 'Short text' }, { v: 'textarea', l: 'Long text' },
  { v: 'email', l: 'Email' }, { v: 'phone', l: 'Phone' },
  { v: 'date', l: 'Date' }, { v: 'number', l: 'Number' },
  { v: 'select', l: 'Dropdown' }, { v: 'multiselect', l: 'Multi-select' },
  { v: 'checkbox', l: 'Checkbox' }, { v: 'file', l: 'File upload' },
  { v: 'address', l: 'Address' }, { v: 'section_header', l: 'Section header' },
];

const slug = (s) => (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 60);
const newField = () => ({ type: 'text', label: 'Untitled', key: '', required: false, options: [], help: '' });
const newSection = () => ({ title: 'Section', fields: [newField()] });

export default function IntakePage() {
  const [templates, setTemplates] = React.useState([]);
  const [submissions, setSubmissions] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [tab, setTab] = React.useState('templates');
  const [builderOpen, setBuilderOpen] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [inviting, setInviting] = React.useState(null);
  const [viewingSubmission, setViewingSubmission] = React.useState(null);

  const load = React.useCallback(() => {
    setLoading(true);
    Promise.all([intakeApi.listTemplates({ include_inactive: true }),
                 intakeApi.listSubmissions()])
      .then(([t, s]) => { setTemplates(t.items || []); setSubmissions(s.items || []); })
      .catch(() => toast.error('Failed to load intake'))
      .finally(() => setLoading(false));
  }, []);
  React.useEffect(load, [load]);

  const doDelete = async (id) => {
    if (!window.confirm('Delete this template? (Deactivates if submissions exist)')) return;
    try { await intakeApi.deleteTemplate(id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };

  return (
    <div className="p-8" data-testid="intake-page">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl font-bold text-[#1A3A5C]">Intake forms</h1>
          <p className="mt-1 text-sm text-slate-500">Drag-and-drop questionnaires per subclass, client-fillable in the portal, auto-populated into the matter file.</p>
        </div>
        <Button onClick={() => { setEditingId(null); setBuilderOpen(true); }} data-testid="intake-new-template">
          <Plus className="mr-2 h-4 w-4" /> New template
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="intake-tabs">
          <TabsTrigger value="templates" data-testid="intake-tab-templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="submissions" data-testid="intake-tab-submissions">Submissions ({submissions.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="templates" className="mt-4">
          {loading ? <div className="flex justify-center p-10"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
           : templates.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-slate-500"><FileCheck className="mx-auto mb-2 h-10 w-10 text-slate-300" />No templates yet. Build your first questionnaire.</CardContent></Card>
           : <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {templates.map((t) => (
                <Card key={t.id} className={t.active ? '' : 'opacity-60'} data-testid={`intake-template-card-${t.id}`}>
                  <CardContent className="p-5">
                    <div className="mb-2 flex items-start justify-between gap-2">
                      <div>
                        <div className="font-serif text-lg font-semibold text-[#1A3A5C]">{t.name}</div>
                        {t.subclass && <div className="text-xs text-slate-500">Subclass {t.subclass}</div>}
                      </div>
                      {!t.active && <Badge className="border border-slate-200 bg-slate-50 text-slate-500">Inactive</Badge>}
                    </div>
                    {t.description && <p className="mb-3 text-xs text-slate-500">{t.description}</p>}
                    <div className="mb-3 text-xs text-slate-500">
                      {t.sections?.length || 0} section{(t.sections?.length || 0) === 1 ? '' : 's'} · {(t.sections || []).reduce((s, x) => s + (x.fields?.length || 0), 0)} fields
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button size="sm" variant="outline" onClick={() => setInviting(t)} data-testid={`intake-send-${t.id}`}><Send className="mr-1 h-3 w-3" />Send to client</Button>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingId(t.id); setBuilderOpen(true); }} data-testid={`intake-edit-${t.id}`}><Edit className="h-3 w-3" /></Button>
                      <Button size="sm" variant="ghost" onClick={() => doDelete(t.id)} data-testid={`intake-delete-${t.id}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          }
        </TabsContent>

        <TabsContent value="submissions" className="mt-4">
          {submissions.length === 0 ? <Card><CardContent className="p-8 text-center text-sm text-slate-500"><Inbox className="mx-auto mb-2 h-10 w-10 text-slate-300" />No submissions yet.</CardContent></Card>
           : <Card><CardContent className="p-0">
             <table className="w-full text-sm">
               <thead className="border-b border-slate-100 text-left text-xs uppercase tracking-wider text-slate-500">
                 <tr><th className="px-4 py-3">Applicant</th><th className="px-4 py-3">Template</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Submitted</th><th className="px-4 py-3" /></tr>
               </thead>
               <tbody>
                 {submissions.map((s) => (
                   <tr key={s.id} className="border-b border-slate-50" data-testid={`intake-submission-${s.id}`}>
                     <td className="px-4 py-3 font-medium text-[#1A3A5C]"><div>{s.applicant_name}</div><div className="text-xs text-slate-500">{s.applicant_email}</div></td>
                     <td className="px-4 py-3 text-slate-700">{s.template_name || '—'}</td>
                     <td className="px-4 py-3"><Badge className={`border ${
                       s.status === 'submitted' ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                       : s.status === 'applied' ? 'border-blue-200 bg-blue-50 text-blue-700'
                       : s.status === 'in_progress' ? 'border-amber-200 bg-amber-50 text-amber-700'
                       : 'border-slate-200 bg-slate-50 text-slate-700'
                     }`}>{s.status}</Badge></td>
                     <td className="px-4 py-3 text-xs text-slate-500">{s.submitted_at ? new Date(s.submitted_at).toLocaleString('en-AU') : '—'}</td>
                     <td className="px-4 py-3 text-right"><Button size="sm" variant="outline" onClick={() => setViewingSubmission(s.id)} data-testid={`intake-view-sub-${s.id}`}>View</Button></td>
                   </tr>
                 ))}
               </tbody>
             </table>
           </CardContent></Card>
          }
        </TabsContent>
      </Tabs>

      <TemplateBuilderDialog
        open={builderOpen}
        templateId={editingId}
        onClose={() => { setBuilderOpen(false); setEditingId(null); }}
        onSaved={load}
      />
      <InviteDialog
        template={inviting}
        onClose={() => setInviting(null)}
        onSent={load}
      />
      <SubmissionDialog
        submissionId={viewingSubmission}
        onClose={() => setViewingSubmission(null)}
        onApplied={load}
      />
    </div>
  );
}

function TemplateBuilderDialog({ open, templateId, onClose, onSaved }) {
  const [form, setForm] = React.useState({ name: '', subclass: '', description: '', sections: [newSection()], active: true });
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!open) return;
    if (templateId) intakeApi.getTemplate(templateId).then(setForm);
    else setForm({ name: '', subclass: '', description: '', sections: [newSection()], active: true });
  }, [open, templateId]);

  const patchSection = (i, patch) => {
    const s = [...form.sections]; s[i] = { ...s[i], ...patch }; setForm({ ...form, sections: s });
  };
  const patchField = (si, fi, patch) => {
    const s = [...form.sections];
    const f = [...s[si].fields]; f[fi] = { ...f[fi], ...patch };
    // Auto-slug key when label changes
    if (patch.label !== undefined && !f[fi].key_manual) f[fi].key = slug(patch.label);
    s[si] = { ...s[si], fields: f }; setForm({ ...form, sections: s });
  };
  const addSection = () => setForm({ ...form, sections: [...form.sections, newSection()] });
  const removeSection = (i) => setForm({ ...form, sections: form.sections.filter((_, x) => x !== i) });
  const addField = (si) => {
    const s = [...form.sections];
    s[si] = { ...s[si], fields: [...s[si].fields, newField()] };
    setForm({ ...form, sections: s });
  };
  const removeField = (si, fi) => {
    const s = [...form.sections];
    s[si] = { ...s[si], fields: s[si].fields.filter((_, x) => x !== fi) };
    setForm({ ...form, sections: s });
  };
  const moveField = (si, fi, delta) => {
    const s = [...form.sections];
    const f = [...s[si].fields];
    const to = fi + delta; if (to < 0 || to >= f.length) return;
    [f[fi], f[to]] = [f[to], f[fi]];
    s[si] = { ...s[si], fields: f }; setForm({ ...form, sections: s });
  };

  const submit = async () => {
    if (!form.name?.trim()) { toast.error('Template name is required'); return; }
    // Ensure all fields have keys
    const payload = { ...form, sections: form.sections.map((s) => ({ ...s, fields: (s.fields || []).map((f) => ({ ...f, key: f.key || slug(f.label) || `field_${Math.random().toString(36).slice(2, 8)}` })) })) };
    setBusy(true);
    try {
      if (templateId) await intakeApi.updateTemplate(templateId, payload);
      else await intakeApi.createTemplate(payload);
      toast.success('Template saved'); onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto" data-testid="intake-builder-dialog">
        <DialogHeader><DialogTitle>{templateId ? 'Edit template' : 'New intake template'}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid gap-3 md:grid-cols-3">
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Name</label>
              <Input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="intake-builder-name" />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Subclass</label>
              <Input value={form.subclass || ''} onChange={(e) => setForm({ ...form, subclass: e.target.value })} placeholder="189, 482, 500..." data-testid="intake-builder-subclass" />
            </div>
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Description (shown to the client)</label>
            <Textarea rows={2} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>

          <div className="space-y-4">
            {(form.sections || []).map((sec, si) => (
              <div key={si} className="rounded-lg border border-slate-200 bg-white p-4" data-testid={`intake-builder-section-${si}`}>
                <div className="mb-3 flex items-center gap-2">
                  <GripVertical className="h-4 w-4 text-slate-300" />
                  <Input className="flex-1 font-semibold" value={sec.title} onChange={(e) => patchSection(si, { title: e.target.value })} data-testid={`intake-builder-section-title-${si}`} />
                  <Button size="sm" variant="ghost" onClick={() => removeSection(si)} data-testid={`intake-builder-section-remove-${si}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                </div>
                <div className="space-y-2">
                  {(sec.fields || []).map((f, fi) => (
                    <div key={fi} className="rounded-md border border-slate-100 bg-slate-50 p-3" data-testid={`intake-builder-field-${si}-${fi}`}>
                      <div className="grid gap-2 md:grid-cols-12 md:items-center">
                        <div className="md:col-span-4">
                          <Input value={f.label} onChange={(e) => patchField(si, fi, { label: e.target.value })} placeholder="Field label" data-testid={`intake-builder-field-label-${si}-${fi}`} />
                        </div>
                        <div className="md:col-span-3">
                          <Select value={f.type} onValueChange={(v) => patchField(si, fi, { type: v })}>
                            <SelectTrigger data-testid={`intake-builder-field-type-${si}-${fi}`}><SelectValue /></SelectTrigger>
                            <SelectContent>{FIELD_TYPES.map((x) => <SelectItem key={x.v} value={x.v}>{x.l}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div className="md:col-span-3">
                          <Input value={f.key || ''} onChange={(e) => patchField(si, fi, { key: e.target.value, key_manual: true })} placeholder="matter_field_key" className="font-mono text-xs" />
                        </div>
                        <label className="md:col-span-1 flex items-center gap-1 text-xs">
                          <input type="checkbox" checked={!!f.required} onChange={(e) => patchField(si, fi, { required: e.target.checked })} /> Req
                        </label>
                        <div className="md:col-span-1 flex justify-end gap-0">
                          <Button size="icon" variant="ghost" onClick={() => moveField(si, fi, -1)}><ArrowUp className="h-3 w-3" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => moveField(si, fi, 1)}><ArrowDown className="h-3 w-3" /></Button>
                          <Button size="icon" variant="ghost" onClick={() => removeField(si, fi)} data-testid={`intake-builder-field-del-${si}-${fi}`}><Trash2 className="h-3 w-3 text-red-500" /></Button>
                        </div>
                      </div>
                      {(f.type === 'select' || f.type === 'multiselect') && (
                        <Input className="mt-2" value={(f.options || []).join(', ')} onChange={(e) => patchField(si, fi, { options: e.target.value.split(',').map((x) => x.trim()).filter(Boolean) })} placeholder="Option 1, Option 2, ..." />
                      )}
                    </div>
                  ))}
                  <Button size="sm" variant="outline" onClick={() => addField(si)} data-testid={`intake-builder-add-field-${si}`}><Plus className="mr-1 h-3 w-3" /> Add field</Button>
                </div>
              </div>
            ))}
            <Button variant="outline" onClick={addSection} data-testid="intake-builder-add-section"><Plus className="mr-1 h-3 w-3" /> Add section</Button>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="intake-builder-save">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Save template</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InviteDialog({ template, onClose, onSent }) {
  const [form, setForm] = React.useState({ applicant_name: '', applicant_email: '', emergency_contact_name: '', emergency_contact_email: '' });
  const [busy, setBusy] = React.useState(false);
  const [result, setResult] = React.useState(null);
  React.useEffect(() => { if (template) { setForm({ applicant_name: '', applicant_email: '', emergency_contact_name: '', emergency_contact_email: '' }); setResult(null); } }, [template]);
  if (!template) return null;
  const submit = async () => {
    if (!form.applicant_name?.trim() || !form.applicant_email?.trim()) { toast.error('Name and email required'); return; }
    setBusy(true);
    try {
      const payload = { template_id: template.id, applicant_name: form.applicant_name, applicant_email: form.applicant_email };
      if (form.emergency_contact_email) {
        payload.emergency_contact_email = form.emergency_contact_email;
        payload.emergency_contact_name = form.emergency_contact_name || undefined;
      }
      const r = await intakeApi.invite(payload);
      setResult(r); toast.success(r.cc_emergency ? 'Invite created + emergency contact CC\'d' : 'Invite created'); onSent();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Invite failed'); }
    finally { setBusy(false); }
  };
  const link = result ? `${window.location.origin}${result.portal_link}` : '';
  return (
    <Dialog open={!!template} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="intake-invite-dialog">
        <DialogHeader><DialogTitle>Send intake — {template.name}</DialogTitle></DialogHeader>
        {!result ? (
          <>
            <div className="space-y-3">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Applicant name</label>
                <Input value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} data-testid="intake-invite-name" />
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-slate-500">Applicant email</label>
                <Input type="email" value={form.applicant_email} onChange={(e) => setForm({ ...form, applicant_email: e.target.value })} data-testid="intake-invite-email" />
              </div>
              <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                <div className="mb-1 text-xs font-semibold uppercase tracking-wider text-slate-500">Emergency contact CC (optional)</div>
                <div className="grid gap-2 md:grid-cols-2">
                  <Input value={form.emergency_contact_name} onChange={(e) => setForm({ ...form, emergency_contact_name: e.target.value })} placeholder="Name" data-testid="intake-invite-ec-name" />
                  <Input type="email" value={form.emergency_contact_email} onChange={(e) => setForm({ ...form, emergency_contact_email: e.target.value })} placeholder="Email" data-testid="intake-invite-ec-email" />
                </div>
                <div className="mt-1 text-xs text-slate-500">If provided, the invite email will CC the emergency contact.</div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button onClick={submit} disabled={busy} data-testid="intake-invite-submit">{busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Create link</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <div className="space-y-3">
              <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
                <div className="mb-1 font-semibold">Portal link ready</div>
                <div className="break-all font-mono text-xs" data-testid="intake-invite-link">{link}</div>
              </div>
              <Button variant="outline" onClick={() => { navigator.clipboard.writeText(link); toast.success('Copied'); }} data-testid="intake-invite-copy">
                <Link2 className="mr-1 h-3 w-3" /> Copy link
              </Button>
            </div>
            <DialogFooter><Button onClick={onClose}>Done</Button></DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function SubmissionDialog({ submissionId, onClose, onApplied }) {
  const [sub, setSub] = React.useState(null);
  const [docs, setDocs] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => {
    if (!submissionId) return;
    setLoading(true);
    Promise.all([intakeApi.getSubmission(submissionId),
                 intakeApi.listSubmissionDocs(submissionId).catch(() => ({ items: [] }))])
      .then(([s, d]) => { setSub(s); setDocs(d.items || []); })
      .finally(() => setLoading(false));
  }, [submissionId]);
  const apply = async (create = true) => {
    setBusy(true);
    try {
      const r = await intakeApi.applySubmission(submissionId, { create_matter: create });
      toast.success(`Matter ${r.matter_ref || r.matter_id} populated`);
      onApplied(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!submissionId} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="intake-submission-dialog">
        <DialogHeader><DialogTitle>Submission — {sub?.applicant_name}</DialogTitle></DialogHeader>
        {loading || !sub ? <div className="flex justify-center p-6"><Loader2 className="h-6 w-6 animate-spin text-[#2ECC9A]" /></div>
         : <div className="space-y-3">
             <div className="text-xs text-slate-500">Template: {sub.template_name} · Status: <span className="font-semibold text-[#1A3A5C]">{sub.status}</span>{sub.auto_assigned_to_name ? <> · Auto-routed to <span className="font-semibold text-emerald-700">{sub.auto_assigned_to_name}</span></> : null}</div>
             <div>
               <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Answers</div>
               <div className="rounded-md border border-slate-200">
                 {Object.entries(sub.answers || {}).map(([k, v]) => (
                   <div key={k} className="border-b border-slate-50 px-3 py-2 text-sm last:border-b-0">
                     <div className="text-xs text-slate-500">{k}</div>
                     <div className="font-medium text-[#1A3A5C]">{typeof v === 'object' ? JSON.stringify(v) : String(v || '—')}</div>
                   </div>
                 ))}
                 {Object.keys(sub.answers || {}).length === 0 && <div className="p-3 text-center text-sm text-slate-500">No answers yet</div>}
               </div>
             </div>
             {docs.length > 0 && (
               <div>
                 <div className="mb-1 text-xs font-semibold uppercase text-slate-500">Uploaded documents ({docs.length})</div>
                 <div className="rounded-md border border-slate-200" data-testid="intake-submission-docs">
                   {docs.map((d) => (
                     <div key={d.id} className="flex items-center justify-between border-b border-slate-50 px-3 py-2 text-sm last:border-b-0" data-testid={`intake-submission-doc-${d.id}`}>
                       <div>
                         <div className="font-medium text-[#1A3A5C]">{d.file_name}</div>
                         <div className="text-xs text-slate-500">{d.label ? `${d.label} · ` : ''}{(d.size_bytes / 1024).toFixed(1)} KB</div>
                       </div>
                       <a href={`${API_BASE_INTAKE}/intake/docs/${d.id}/download`} target="_blank" rel="noreferrer"
                          onClick={(e) => { e.preventDefault(); intakeApi.downloadDoc(d.id).then((blob) => {
                              const url = URL.createObjectURL(blob);
                              const a = document.createElement('a');
                              a.href = url; a.download = d.file_name;
                              document.body.appendChild(a); a.click(); a.remove();
                              URL.revokeObjectURL(url);
                            }); }}>
                         <Button size="sm" variant="outline">Download</Button>
                       </a>
                     </div>
                   ))}
                 </div>
               </div>
             )}
           </div>
        }
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
          {sub?.status === 'submitted' && (
            <Button onClick={() => apply(true)} disabled={busy} data-testid="intake-submission-apply">
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Auto-create matter
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const API_BASE_INTAKE = (process.env.REACT_APP_BACKEND_URL || '') + '/api';
